/**
 * 编辑器右键菜单组件
 * 提供插入图片、流程图、图表等功能
 */
import React, { useEffect, useRef } from 'react';

interface MenuItem {
  icon: string;
  label: string;
  shortcut?: string;
  onClick: () => void;
  divider?: boolean;
}

interface EditorContextMenuProps {
  x: number;
  y: number;
  show: boolean;
  onClose: () => void;
  onInsertMermaid: () => void;
  onInsertTable: () => void;
  onInsertImage?: () => void;         // 新增：插入图片回调
  onInsertPageBreak?: () => void;     // 新增：插入分页符回调
  onDeletePageBreak?: () => void;     // 新增：删除分页符回调
  onDeleteTable?: () => void;
  isInTable?: boolean;
  isInPageBreak?: boolean;            // 新增：是否在分页符上
  hasSelectedText?: boolean;       // 新增：是否有选中文字
  onOptimizeParagraph?: () => void; // 新增：段落优化回调
  onAddAnnotation?: () => void;    // 新增：添加注记回调
  isPreviewMode?: boolean;         // 新增：是否为预览模式
}

const EditorContextMenu: React.FC<EditorContextMenuProps> = ({
  x,
  y,
  show,
  onClose,
  onInsertMermaid,
  onInsertTable,
  onInsertImage,                    // 新增
  onInsertPageBreak,                // 新增：插入分页符
  onDeletePageBreak,                // 新增：删除分页符
  onDeleteTable,
  isInTable = false,
  isInPageBreak = false,            // 新增
  hasSelectedText = false,         // 新增
  onOptimizeParagraph,              // 新增
  onAddAnnotation,                  // 新增
  isPreviewMode = false,            // 新增
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // 点击外部关闭菜单
  useEffect(() => {
    if (!show) return;

    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleScroll = () => {
      onClose();
    };

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('scroll', handleScroll, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [show, onClose]);

  // 按 Esc 关闭菜单
  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [show, onClose]);

  if (!show) return null;

  // 预览模式只显示复制和添加注记
  if (isPreviewMode) {
    const previewMenuItems: MenuItem[] = [
      {
        icon: '📋',
        label: '复制',
        shortcut: 'Ctrl+C',
        onClick: () => {
          document.execCommand('copy');
          onClose();
        },
      },
    ];
    
    // 如果有选中文字且有注记功能，添加注记选项
    if (hasSelectedText && onAddAnnotation) {
      previewMenuItems.push({
        icon: '📝',
        label: '添加注记',
        onClick: () => {
          onAddAnnotation();
          onClose();
        },
        divider: true,
      });
    }
    
    return (
      <div
        ref={menuRef}
        className="fixed z-[9999] bg-white rounded-lg shadow-2xl border border-gray-200 py-1 min-w-[160px]"
        style={{
          left: `${x}px`,
          top: `${y}px`,
        }}
      >
        {previewMenuItems.map((item, index) => (
          <button
            key={index}
            onClick={item.onClick}
            className="w-full px-4 py-2 text-left text-sm hover:bg-blue-50 flex items-center justify-between group transition-colors"
          >
            <span className="flex items-center gap-2">
              <span className="text-base">{item.icon}</span>
              <span className="text-gray-700">{item.label}</span>
            </span>
            {item.shortcut && (
              <span className="text-xs text-gray-400 group-hover:text-gray-500">
                {item.shortcut}
              </span>
            )}
          </button>
        ))}
      </div>
    );
  }

  // 编辑模式显示完整菜单
  const menuItems: MenuItem[] = [
    {
      icon: '📋',
      label: '复制',
      shortcut: 'Ctrl+C',
      onClick: () => {
        document.execCommand('copy');
        onClose();
      },
    },
    {
      icon: '✂️',
      label: '剪切',
      shortcut: 'Ctrl+X',
      onClick: () => {
        document.execCommand('cut');
        onClose();
      },
    },
    {
      icon: '📄',
      label: '粘贴',
      shortcut: 'Ctrl+V',
      onClick: () => {
        document.execCommand('paste');
        onClose();
      },
      divider: true,
    },
  ];

  // 添加插入图片选项（在插入表格上面）
  if (onInsertImage) {
    menuItems.push({
      icon: '🖼️',
      label: '插入图片',
      shortcut: 'Ctrl+Shift+I',
      onClick: () => {
        onInsertImage();
        onClose();
      },
    });
  }

  // 添加插入表格、流程图和分页符
  menuItems.push(
    {
      icon: '📋',
      label: '插入表格',
      shortcut: 'Ctrl+Shift+T',
      onClick: () => {
        onInsertTable();
        onClose();
      },
    },
    {
      icon: '📊',
      label: '插入流程图',
      shortcut: 'Ctrl+Shift+F',
      onClick: () => {
        onInsertMermaid();
        onClose();
      },
    }
  );

  // 添加分页符操作（根据是否在分页符上显示不同选项）
  if (isInPageBreak && onDeletePageBreak) {
    // 在分页符上，显示删除分页符
    menuItems.push({
      icon: '🗑️',
      label: '删除分页符',
      onClick: () => {
        onDeletePageBreak();
        onClose();
      },
      divider: true,
    });
  } else if (!isInPageBreak && onInsertPageBreak) {
    // 不在分页符上，显示插入分页符
    menuItems.push({
      icon: '📄',
      label: '插入分页符',
      onClick: () => {
        onInsertPageBreak();
        onClose();
      },
      divider: true,
    });
  }

  // 如果有选中文字，添加"段落智能优化"和"添加注记"选项
  if (hasSelectedText) {
    if (onOptimizeParagraph) {
      menuItems.push({
        icon: '✨',
        label: '段落智能优化',
        onClick: () => {
          onOptimizeParagraph();
          onClose();
        },
        divider: false,
      });
    }
    
    if (onAddAnnotation) {
      menuItems.push({
        icon: '📝',
        label: '添加注记',
        shortcut: 'Ctrl+Shift+A',
        onClick: () => {
          onAddAnnotation();
          onClose();
        },
        divider: true,
      });
    }
  }

  // 如果在表格内，添加"删除表格"选项
  if (isInTable && onDeleteTable) {
    menuItems.push({
      icon: '🗑️',
      label: '删除表格',
      onClick: () => {
        onDeleteTable();
        onClose();
      },
    });
  }

  return (
    <div
      ref={menuRef}
      className="fixed bg-white rounded-lg shadow-2xl border border-gray-200 py-2 min-w-[220px] z-[9999] animate-in fade-in zoom-in duration-150"
      style={{
        left: `${x}px`,
        top: `${y}px`,
      }}
    >
      {menuItems.map((item, index) => (
        <React.Fragment key={index}>
          <button
            onClick={item.onClick}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center justify-between transition-colors"
          >
            <span className="flex items-center gap-2">
              <span className="text-base">{item.icon}</span>
              <span>{item.label}</span>
            </span>
            {item.shortcut && (
              <span className="text-xs text-gray-400 ml-4">{item.shortcut}</span>
            )}
          </button>
          {item.divider && (
            <div className="h-px bg-gray-200 my-1" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default EditorContextMenu;
